import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import WhatsNewModal from "../WhatsNewModal";
import NewVersionBanner from "../NewVersionBanner";
import OnboardingTour from "../OnboardingTour";
import { useThemeStore, THEME_VARS } from "../../store/themeStore";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { useAuthStore } from "../../store/authStore";
import { api } from "../../lib/api";

function ImpersonationBanner() {
  const impersonating = useAuthStore(s => s.impersonating);
  const currentUser = useAuthStore(s => s.user);
  const stopImpersonation = useAuthStore(s => s.stopImpersonation);
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  if (!impersonating) return null;

  return (
    <div style={{
      background: "#b45309",
      color: "#fff",
      padding: "0.5rem 1rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontSize: "0.8rem",
      fontWeight: 600,
      gap: 12,
      flexShrink: 0,
      zIndex: 200,
    }}>
      {/* En móvil el texto completo rompía en 3 líneas: solo la org, en una */}
      <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <span style={{ width: 7, height: 7, borderRadius: 4, background: "rgba(255,255,255,0.9)", display: "inline-block", flexShrink: 0 }} />
        {isMobile ? (
          <strong style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {currentUser?.organization_name || currentUser?.email}
          </strong>
        ) : (
          <>
            <span style={{ whiteSpace: "nowrap" }}>Sesión de prueba como</span> <strong>{currentUser?.email}</strong>
            {currentUser?.organization_name && (
              <span style={{ opacity: 0.8, fontWeight: 400, whiteSpace: "nowrap" }}>· {currentUser.organization_name}</span>
            )}
          </>
        )}
      </span>
      <button
        onClick={() => { stopImpersonation(); navigate("/superadmin"); }}
        style={{
          background: "rgba(255,255,255,0.25)",
          border: "1px solid rgba(255,255,255,0.4)",
          borderRadius: 6,
          color: "#fff",
          padding: "3px 12px",
          fontSize: "0.75rem",
          fontWeight: 700,
          cursor: "pointer",
          whiteSpace: "nowrap",
          transition: "background 0.15s",
        }}
        onMouseEnter={e => (e.currentTarget.style.background = "rgba(255,255,255,0.38)")}
        onMouseLeave={e => (e.currentTarget.style.background = "rgba(255,255,255,0.25)")}
      >
        {isMobile ? "← Salir" : "← Volver a HelixScan"}
      </button>
    </div>
  );
}

function hexToRgb(hex: string): [number, number, number] | null {
  const m = hex.match(/^#([0-9a-f]{6})$/i);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Mix color with white (light mode) */
function mixLight(c: number, ratio: number) {
  return Math.round(c * ratio + 255 * (1 - ratio));
}

/** Mix color with near-black (dark mode) */
function mixDark(c: number, ratio: number) {
  return Math.round(c * ratio + 7 * (1 - ratio));
}

function applyOrgColor(color: string | null | undefined, theme: "light" | "dark") {
  const root = document.documentElement;
  const G = color || "#0891b2";
  const rgb = hexToRgb(G);

  root.style.setProperty("--clr-org-primary", G);
  root.style.setProperty("--clr-nav-active", G);
  // Tinte del color de marca para elementos activos y badges (más presencia).
  root.style.setProperty("--clr-nav-active-bg", theme === "light" ? `${G}22` : `${G}2e`);
  root.style.setProperty("--clr-accent-soft", theme === "light" ? `${G}18` : `${G}26`);

  if (rgb) {
    const [r, g, b] = rgb;
    // v3 liquid glass: el color de la org tiñe el papel con presencia (10%) y
    // alimenta los glows estáticos — el vidrio necesita color debajo para
    // despegarse y que las cards se lean. Las sombras quedan NEUTRAS
    // (las define THEME_VARS) — nada de sombras de color, era marca de slop.
    if (theme === "light") {
      root.style.setProperty("--clr-bg", `rgb(${mixLight(r,.10)},${mixLight(g,.10)},${mixLight(b,.10)})`);
      root.style.setProperty("--clr-bg-grad", `rgb(${mixLight(r,.10)},${mixLight(g,.10)},${mixLight(b,.10)})`);
      root.style.setProperty("--clr-orb1", `rgba(${r},${g},${b},0.30)`);
      root.style.setProperty("--clr-orb2", "rgba(124,58,237,0.14)");
    } else {
      root.style.setProperty("--clr-bg", `rgb(${mixDark(r,.09)},${mixDark(g,.09)},${mixDark(b,.09)})`);
      root.style.setProperty("--clr-bg-grad", `rgb(${mixDark(r,.09)},${mixDark(g,.09)},${mixDark(b,.09)})`);
      root.style.setProperty("--clr-orb1", `rgba(${r},${g},${b},0.20)`);
      root.style.setProperty("--clr-orb2", "rgba(124,58,237,0.10)");
    }
  }
}

export default function AppShell() {
  const theme = useThemeStore(s => s.theme);
  const { isMobile } = useBreakpoint();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const primaryColor = useAuthStore(s => s.user?.primary_color);
  const orgId = useAuthStore(s => s.user?.organization_id);
  const role = useAuthStore(s => s.user?.role);
  const updateBranding = useAuthStore(s => s.updateBranding);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Asistente de alta: si la organización aún no tiene portfolio, llevar al
  // wizard de configuración inicial (solo usuarios que pueden editar).
  useEffect(() => {
    if (!orgId || (role !== "admin" && role !== "analyst")) return;
    if (localStorage.getItem(`hx_onb_skip:${orgId}`)) return;
    api.get<{ needs_onboarding: boolean }>("/portfolio/onboarding-status")
      .then(d => {
        if (d.needs_onboarding && pathname !== "/onboarding") navigate("/onboarding");
      })
      .catch(() => {});
  }, [orgId, role]);

  // Refresh branding from server on mount so stale cached sessions pick up changes
  useEffect(() => {
    if (!orgId) return;
    api.get<{ primary_color: string | null; logo_url: string | null }>(`/organizations/${orgId}/branding`)
      .then(d => {
        updateBranding(d.primary_color, d.logo_url);
        applyOrgColor(d.primary_color, useThemeStore.getState().theme);
      })
      .catch(() => {});
  }, [orgId]);

  useEffect(() => {
    const root = document.documentElement;
    const vars = THEME_VARS[theme];
    Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
    applyOrgColor(primaryColor, theme);
  }, [theme, primaryColor]);

  useEffect(() => {
    if (!isMobile) setSidebarOpen(false);
  }, [isMobile]);

  return (
    <div style={{
      display: "flex", height: "100vh",
      fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
      background: "var(--clr-bg)",
      position: "relative", overflow: "hidden",
    }}>
      {/* Glows estáticos de fondo (lo que el cristal refracta) — como la landing */}
      <div aria-hidden style={{
        position: "absolute", top: "-12%", right: "8%", width: 620, height: 620,
        background: "radial-gradient(circle closest-side, var(--clr-orb1), transparent 70%)",
        filter: "blur(60px)", pointerEvents: "none", zIndex: 0,
      }} />
      <div aria-hidden style={{
        position: "absolute", bottom: "-18%", left: "22%", width: 560, height: 560,
        background: "radial-gradient(circle closest-side, var(--clr-orb2), transparent 70%)",
        filter: "blur(60px)", pointerEvents: "none", zIndex: 0,
      }} />
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 350,  // sobre el TopBar (300), bajo el sidebar móvil (400)
          }}
        />
      )}

      <Sidebar
        isMobile={isMobile}
        isOpen={!isMobile || sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* minWidth: 0 — sin esto las tablas anchas impiden encoger el flex item y rompen el overflowX de las páginas */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0, position: "relative", zIndex: 1 }}>
        <ImpersonationBanner />
        <TopBar
          showMenuToggle={isMobile}
          onMenuToggle={() => setSidebarOpen(o => !o)}
        />
        <main style={{ flex: 1, overflowY: "auto", background: "transparent" }}>
          <Outlet />
        </main>
      </div>
      <WhatsNewModal />
      <NewVersionBanner />
      <OnboardingTour />
    </div>
  );
}
